import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import translations from "../utils/translations";
import { API_BASE_URL } from "../utils/constants";
import session from "../utils/session";

const STORAGE_KEY = "app_language";

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  t: () => "",
});

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Initially load from localStorage for instant UI
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "kn" ? "kn" : "en";
  });

  // Fetch language preference from backend on mount
  useEffect(() => {
    const fetchLanguage = async () => {
      try {
        const userData = session.getUser();
        if (!userData) return; // Not logged in, use localStorage value

        const response = await fetch(`${API_BASE_URL}/settings/language`, {
          credentials: "include", // Send httpOnly cookie for auth
        });

        if (response.ok) {
          const data = await response.json();
          const serverLanguage = data.language === "kn" ? "kn" : "en";

          // Update state and localStorage if different from server
          if (serverLanguage !== language) {
            localStorage.setItem(STORAGE_KEY, serverLanguage);
            setLanguageState(serverLanguage);
          }
        }
      } catch (error) {
        console.error("Failed to fetch language preference:", error);
        // Continue using localStorage value on error
      }
    };

    fetchLanguage();
  }, []); // Run once on mount

  const setLanguage = useCallback(async (lang) => {
    const validated = lang === "kn" ? "kn" : "en";

    // Update state and localStorage immediately for instant UI feedback
    localStorage.setItem(STORAGE_KEY, validated);
    setLanguageState(validated);

    // Save to backend asynchronously
    try {
      const userData = session.getUser();
      if (!userData) return; // Not logged in, localStorage is enough

      await fetch(`${API_BASE_URL}/settings/language`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Send httpOnly cookie for auth
        body: JSON.stringify({ language: validated }),
      });
    } catch (error) {
      console.error("Failed to save language preference:", error);
      // Still keep the local change even if server update fails
    }
  }, []);

  /**
   * Look up a translation by dot-notation key, e.g. t('sidebar.dashboard').
   * Falls back to the English string, then to the key itself.
   */
  const t = useCallback(
    (key) => {
      const parts = key.split(".");
      let value = translations[language];
      for (const part of parts) {
        if (value == null) break;
        value = value[part];
      }
      if (value != null && typeof value !== "object") return value;

      // Fallback to English
      let fallback = translations.en;
      for (const part of parts) {
        if (fallback == null) break;
        fallback = fallback[part];
      }
      if (fallback != null && typeof fallback !== "object") return fallback;

      return key;
    },
    [language],
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
